import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Archive,
  ArrowRight,
  ClipboardList,
  Eye,
  PlayCircle,
  RefreshCw,
  RotateCcw,
  SplitSquareHorizontal,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import "./IntakeKanbanColors.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const COLUMNS = [

  {
    id: "reviewing",
    title: "Sales Ops Reviewing",
    description: "Submitted requests being reviewed, assigned, or corrected by Sales Ops.",
    statuses: ["awaiting_isr_review", "reviewing", "isr_reviewing"],
  },
  {
    id: "released",
    title: "Waiting Project Creation",
    description: "Approved by Sales Ops and waiting for Graphics Admin to create project record(s).",
    statuses: ["submitted_to_graphics"],
  },
  {
    id: "converted",
    title: "Project Created",
    description: "Converted requests remain here for 7 days, then drop from the Intake Queue.",
    statuses: ["converted"],
  },
];

const formatDate = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return "";
  }
};

const projectIdsFor = (item) => Array.from(new Set([
  ...(Array.isArray(item.converted_project_ids) ? item.converted_project_ids : []),
  ...(Array.isArray(item.project_ids) ? item.project_ids : []),
].filter(Boolean)));

const identityValues = (user) => {
  const values = new Set([
    user?.id,
    user?.full_name,
    user?.username,
    user?.email,
  ].map((value) => String(value || "").trim().toLowerCase()).filter(Boolean));

  [user?.username, user?.email].forEach((value) => {
    const text = String(value || "").trim().toLowerCase();
    if (text.includes("@")) values.add(text.split("@", 1)[0]);
  });

  return values;
};

const matchesUser = (value, user) => {
  const text = String(value || "").trim().toLowerCase();
  return Boolean(text && identityValues(user).has(text));
};

const intakeAssignedToUser = (item, user, role) => {
  if (role === "graphics_admin" && item.status === "submitted_to_graphics") return true;

  return (
    matchesUser(item.assigned_isr, user)
    || matchesUser(item.assigned_osr, user)
    || matchesUser(item.contact_email, user)
    || matchesUser(item.review_started_by, user)
    || matchesUser(item.created_by, user)
    || matchesUser(item.created_by_email, user)
  );
};

export const IntakeSubmissionsPageV9 = () => {
  const {
    isAdmin,
    isManager,
    canViewAllProjects,
    user,
    role,
    roleLabel,
  } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [options, setOptions] = useState({ isr_list: [], osr_list: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [viewScope, setViewScope] = useState(() => (
    new URLSearchParams(window.location.search).get("view") === "all"
      ? "all"
      : "mine"
  ));

  const pageDescription = canViewAllProjects && viewScope === "all"
    ? `All intake requests visible to ${roleLabel}.`
    : "Requests assigned to you or currently waiting for your role's action.";

  const changeViewScope = (scope) => {
    const nextScope = scope === "all" ? "all" : "mine";
    const params = new URLSearchParams(window.location.search);

    params.set("view", nextScope);
    setViewScope(nextScope);

    navigate(
      {
        pathname: window.location.pathname,
        search: `?${params.toString()}`,
      },
      { replace: true },
    );
  };

  const canReassign = isAdmin || isManager;
  const canCancelSubmission = isAdmin || isManager || role === "sales_rep";

  const fetchItems = async () => {
    setLoading(true);
    try {
      const [queueResponse, optionResponse] = await Promise.all([
        axios.get(`${API}/intake-submissions`, authHeaders()),
        axios.get(`${API}/public/intake-options`),
      ]);
      setItems(queueResponse.data || []);
      setOptions(optionResponse.data || { isr_list: [], osr_list: [] });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load intake queue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const scopedItems = useMemo(() => {
    if (!canViewAllProjects || viewScope === "all") return items;
    return items.filter((item) => intakeAssignedToUser(item, user, role));
  }, [canViewAllProjects, items, role, user, viewScope]);

  const visibleItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return scopedItems;
    return scopedItems.filter((item) => [
      item.company_name,
      item.contact_name,
      item.contact_email,
      item.confirmation_number,
      item.assigned_isr,
      item.assigned_osr,
      item.manufacturer_name,
    ].some((value) => String(value || "").toLowerCase().includes(term)));
  }, [scopedItems, search]);

  const itemsForColumn = (column) => visibleItems.filter((item) => column.statuses.includes(item.status));

  const assign = async (item, field, value) => {
    setBusyId(item.id);
    try {
      await axios.put(`${API}/intake-submissions/${item.id}/assign`, {
        assigned_isr: field === "assigned_isr" ? value : item.assigned_isr,
        assigned_osr: field === "assigned_osr" ? value : item.assigned_osr,
        notify: false,
      }, authHeaders());
      toast.success("Assignment updated");
      await fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update assignment");
    } finally {
      setBusyId(null);
    }
  };

  const openReview = async (item) => {
    setBusyId(item.id);
    try {
      if (["new", "reviewing", "awaiting_isr_review"].includes(item.status)) {
        await axios.post(`${API}/intake-submissions/${item.id}/start-review`, {}, authHeaders());
      }
      navigate(`/intake-submissions/${item.id}/review`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start Sales Ops review");
      setBusyId(null);
    }
  };

  const convert = async (item) => {
    if (!window.confirm("Convert this request into one Graphics project per SKU?")) return;
    setBusyId(item.id);
    try {
      const response = await axios.post(
        `${API}/intake-submissions/${item.id}/convert-v4`,
        { split_by_sku: true },
        authHeaders(),
      );
      toast.success(`Created ${response.data.created_count} project(s)`);
      await fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert request");
    } finally {
      setBusyId(null);
    }
  };

  const cancelSubmission = async (item) => {
    const reason = window.prompt("Reason for cancelling submission and returning it to Sales Ops Reviewing:", "Needs additional information before project creation");
    if (reason === null) return;
    setBusyId(item.id);
    try {
      await axios.post(
        `${API}/intake-submissions/${item.id}/cancel-submission-v4`,
        { reason: reason.trim() },
        authHeaders(),
      );
      toast.success("Submission cancelled and returned to Sales Ops Reviewing");
      await fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel submission");
    } finally {
      setBusyId(null);
    }
  };

  const archive = async (item) => {
    if (!window.confirm("Archive this design request?")) return;
    setBusyId(item.id);
    try {
      await axios.post(`${API}/intake-submissions/${item.id}/archive-v4`, {}, authHeaders());
      toast.success("Request archived");
      await fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to archive request");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="animate-fade-in space-y-6 intake-kanban-color-board">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <ClipboardList size={30} /> Intake Queue
          </h1>
          <p className="text-muted-foreground mt-1">{pageDescription}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => navigate("/intake")}
            className="px-4 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium"
          >
            New Design Request
          </button>
          <button
            type="button"
            onClick={fetchItems}
            className="flex items-center gap-2 px-4 py-2.5 bg-secondary rounded-lg"
          >
            <RefreshCw size={18} /> Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="w-full max-w-xl flex-1 min-w-[280px]">
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={canViewAllProjects && viewScope === "all" ? "Search all intake requests..." : "Search my intake requests..."}
            className="w-full px-4 py-2.5 bg-secondary border border-border rounded-lg"
          />
        </div>
        {canViewAllProjects && (
          <div className="inline-flex rounded-lg border border-border bg-card p-1">
            <button
              type="button"
              onClick={() => changeViewScope("mine")}
              className={`rounded-md px-3 py-1.5 text-sm ${viewScope === "mine" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}
            >
              My Projects
            </button>
            <button
              type="button"
              onClick={() => changeViewScope("all")}
              className={`rounded-md px-3 py-1.5 text-sm ${viewScope === "all" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}
            >
              All Projects
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-muted-foreground animate-pulse">Loading intake queue...</div>
      ) : (
        <div className="overflow-x-auto pb-4">
          <div className="grid grid-cols-3 gap-4 min-w-[1200px]">
            {COLUMNS.map((column) => {
              const columnItems = itemsForColumn(column);
              return (
                <section key={column.id} className="bg-card border border-border rounded-xl min-h-[520px]">
                  <div className="p-4 border-b border-border">
                    <div className="flex items-center justify-between gap-3">
                      <h2 className="font-semibold">{column.title}</h2>
                      <span className="inline-flex items-center justify-center min-w-7 h-7 px-2 rounded-full bg-secondary text-sm font-bold">
                        {columnItems.length}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 leading-5">{column.description}</p>
                  </div>

                  <div className="p-3 space-y-3">
                    {columnItems.length === 0 ? (
                      <div className="border border-dashed border-border rounded-lg p-6 text-center text-sm text-muted-foreground">
                        No requests
                      </div>
                    ) : columnItems.map((item) => {
                      const linkedProjectIds = projectIdsFor(item);
                      return (
                        <article key={item.id} className="bg-background border border-border rounded-lg p-4 space-y-3">
                          <div>
                            <h3 className="font-semibold leading-5">{item.company_name}</h3>
                            <p className="text-xs text-muted-foreground mt-1">{item.confirmation_number}</p>
                          </div>

                          <div className="text-xs space-y-1">
                            <p><span className="text-muted-foreground">Sales Ops:</span> {item.assigned_isr || "Unassigned"}</p>
                            <p><span className="text-muted-foreground">OSR:</span> {item.assigned_osr || "Unassigned"}</p>
                            <p><span className="text-muted-foreground">SKUs:</span> {item.sku_count || 0}</p>
                            {item.status === "converted" && linkedProjectIds.length > 0 && (
                              <p><span className="text-muted-foreground">Projects:</span> {linkedProjectIds.length}</p>
                            )}
                            <p><span className="text-muted-foreground">Updated:</span> {formatDate(item.updated_at || item.submitted_at)}</p>
                          </div>

                          {canReassign && (
                            <div className="space-y-2 pt-2 border-t border-border">
                              <select
                                value={item.assigned_isr || ""}
                                disabled={busyId === item.id}
                                onChange={(event) => assign(item, "assigned_isr", event.target.value)}
                                className="w-full px-2 py-2 bg-secondary border border-border rounded-md text-xs"
                              >
                                <option value="">Unassigned Sales Ops</option>
                                {(options.isr_list || []).map((name) => <option key={name} value={name}>{name}</option>)}
                              </select>
                              <select
                                value={item.assigned_osr || ""}
                                disabled={busyId === item.id}
                                onChange={(event) => assign(item, "assigned_osr", event.target.value)}
                                className="w-full px-2 py-2 bg-secondary border border-border rounded-md text-xs"
                              >
                                <option value="">Unassigned OSR</option>
                                {(options.osr_list || []).map((name) => <option key={name} value={name}>{name}</option>)}
                              </select>
                            </div>
                          )}

                          <div className="flex flex-wrap gap-2 pt-2">
                            {["new", "reviewing", "awaiting_isr_review", "isr_reviewing"].includes(item.status) && (
                              <button
                                type="button"
                                onClick={() => openReview(item)}
                                disabled={busyId === item.id}
                                className="flex items-center gap-1 px-3 py-2 bg-primary text-primary-foreground rounded-md text-xs font-medium disabled:opacity-50"
                              >
                                {item.status === "isr_reviewing" ? <Eye size={14} /> : <PlayCircle size={14} />}
                                {item.status === "isr_reviewing" ? "Continue Review" : "Start Review"}
                              </button>
                            )}

                            {["submitted_to_graphics", "converted"].includes(item.status) && (
                              <button
                                type="button"
                                onClick={() => navigate(`/intake-submissions/${item.id}/review`)}
                                className="flex items-center gap-1 px-3 py-2 bg-secondary rounded-md text-xs font-medium"
                              >
                                <Eye size={14} /> View
                              </button>
                            )}

                            {isAdmin && item.status === "submitted_to_graphics" && (
                              <button
                                type="button"
                                onClick={() => convert(item)}
                                disabled={busyId === item.id}
                                className="flex items-center gap-1 px-3 py-2 bg-emerald-700 text-white rounded-md text-xs font-medium disabled:opacity-50"
                              >
                                <SplitSquareHorizontal size={14} /> Create Projects
                              </button>
                            )}

                            {canCancelSubmission && item.status === "submitted_to_graphics" && (
                              <button
                                type="button"
                                onClick={() => cancelSubmission(item)}
                                disabled={busyId === item.id}
                                className="flex items-center gap-1 px-3 py-2 bg-amber-950/50 text-amber-300 rounded-md text-xs font-medium disabled:opacity-50"
                              >
                                <RotateCcw size={14} /> Cancel Submission
                              </button>
                            )}

                            {item.status === "converted" && linkedProjectIds.map((projectId, index) => (
                              <button
                                key={projectId}
                                type="button"
                                onClick={() => navigate(`/projects/${projectId}`)}
                                className="flex items-center gap-1 px-3 py-2 bg-secondary rounded-md text-xs font-medium"
                              >
                                {linkedProjectIds.length === 1 ? "Open Project" : `Open Project ${index + 1}`}
                                <ArrowRight size={14} />
                              </button>
                            ))}

                            {isAdmin && item.status !== "archived" && (
                              <button
                                type="button"
                                onClick={() => archive(item)}
                                disabled={busyId === item.id}
                                className="flex items-center gap-1 px-3 py-2 text-red-400 bg-red-950/20 rounded-md text-xs disabled:opacity-50"
                              >
                                <Archive size={14} /> Archive
                              </button>
                            )}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
